import type { ColumnDef } from "@tanstack/table-core";
import { CheckCircle2, ChevronRight } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  PURCHASE_REQUEST_STATUS_COLORS,
  purchaseRequestStatusLabel,
} from "@/constants/purchase-request";
import { DataTable } from "@/lib/data-table";
import { DataTableColumnHeader } from "@/lib/data-table-column-header";
import type { GroupedWorkItems, WorkItemRow } from "@/types";
import { formatDateAR } from "@/utils/date";

const columns: ColumnDef<WorkItemRow>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "sector_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sector" />
    ),
    cell: ({ row }) => row.original.sector_name ?? "-",
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const status = row.original.status;
      const color =
        PURCHASE_REQUEST_STATUS_COLORS[
          status as keyof typeof PURCHASE_REQUEST_STATUS_COLORS
        ] ?? "";
      return (
        <Badge className={color}>{purchaseRequestStatusLabel(status)}</Badge>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "needed_by_on",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fecha límite" />
    ),
    cell: ({ row }) => {
      const deadline = row.original.needed_by_on;
      return deadline ? formatDateAR(deadline) : "-";
    },
    enableSorting: false,
  },
  {
    id: "action",
    cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
    enableSorting: false,
  },
];

interface OrganizationRequestsTableProps {
  group: GroupedWorkItems;
  onRowClick: (row: WorkItemRow) => void;
}

function OrganizationRequestsTable({
  group,
  onRowClick,
}: OrganizationRequestsTableProps) {
  const logoUrl = group.items[0]?.organization.logo_url;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between group/header">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 rounded-xl ring-1 ring-slate-200/50">
            <AvatarImage
              src={logoUrl ?? undefined}
              alt={group.organization.name}
              className="object-cover"
            />
            <AvatarFallback className="rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 text-white text-xs font-semibold">
              {group.organization.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="text-sm font-semibold tracking-tight text-slate-900">
              {group.organization.name}
            </h3>
            <p className="text-xs text-slate-500">
              {group.items.length} solicitud
              {group.items.length !== 1 ? "es" : ""}
            </p>
          </div>
        </div>
        <Badge
          variant="secondary"
          className="text-xs font-medium transition-all duration-300"
        >
          {group.items.length}
        </Badge>
      </div>
      <DataTable
        columns={columns}
        data={group.items}
        manualSorting={false}
        manualPagination={false}
        pagination={false}
        onRowClick={onRowClick}
      />
    </div>
  );
}

interface MyRequestsSectionProps {
  groupedRequests: GroupedWorkItems[];
  onRowClick: (row: WorkItemRow) => void;
}

export function MyRequestsSection({
  groupedRequests,
  onRowClick,
}: MyRequestsSectionProps) {
  if (groupedRequests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 ring-1 ring-emerald-200/50">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">
          Sin solicitudes activas
        </h3>
        <p className="mt-1 text-center text-sm text-slate-500 max-w-sm">
          Todas tus solicitudes fueron completadas o aprobadas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedRequests.map((group) => (
        <OrganizationRequestsTable
          key={group.organization.slug}
          group={group}
          onRowClick={onRowClick}
        />
      ))}
    </div>
  );
}
